import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useParams } from 'react-router';
import { SpeakerDetailsConsumer } from '../../contexts/SpeakerDetailsContext';
import ProgramCard from '../Program/components/ProgramCard';
import { isLiveCheck, isConferenceDayCheck } from '../../utils/helpers';
import './SpeakerDetails.scss';

export const SpeakerDetails = ({ isLoading, speakers, talks, loadSpeakerDetails }) => {
  const history = useHistory();
  const { speakerId } = useParams();
  const details = speakers.find(sp => sp.id === speakerId) || {};
  const filteredTalks = talks.filter(talk => talk.speakers.includes(speakerId));

  const [trigger, setTrigger] = useState(new Date());
  const [isConferenceDay, setIsConferenceDay] = useState(false);

  useEffect(() => {
    loadSpeakerDetails(speakerId);
    setIsConferenceDay(isConferenceDayCheck());

    const handleVisibility = () => {
      if (!document.hidden) setTrigger(new Date());
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setTrigger(new Date());
    }, 1000 * 60);
    return () => clearInterval(interval);
  }, [trigger]);

  return (
    // <div id="speaker-details-section">
    <section className="section sp-form" id="speaker-details">
      <div className="container sp-container">
        {!isLoading && !speakers.some(sp => sp.id === speakerId) ? (
          <h1 className="no-speaker">Oops ! There is no such speaker.</h1>
        ) : (
          <div className="columns is-centered">
            {isLoading ? (
              <i className="fa fa-spinner fa-spin fa-3x fa-fw" style={{ color: '#7500c0' }} />
            ) : (
              <div className="column is-full">
                <div>
                  <button
                    className="button back-btn"
                    onClick={() => history.goBack()}
                    type="button">
                    <span className="fa-stack is-large">
                      <i className="fas fa-circle fa-stack-2x" />
                      <i className="fas fa-angle-left fa-lg fa-stack-1x" />
                    </span>
                    <span className="button-text">Back</span>
                  </button>
                </div>
                <div className="columns speaker-card">
                  <div className="column is-one-third is-half-tablet is-full-mobile">
                    <div className="speaker-photo">
                      <img
                        alt="speaker"
                        className="img"
                        onError={e => {
                          e.target.src = 'imgs/speakers/unknown.png';
                        }}
                        src={details.photo || 'imgs/speakers/unknown.png'}
                      />
                    </div>
                  </div>
                  <div className="column">
                    <div className="info">
                      <h1 className="speaker-title" id="name">
                        {details.name}
                      </h1>
                      <h2 className="speaker-subtitle">
                        {details.company === '---'
                          ? details.country
                          : `${details.company}, ${details.country}`}
                      </h2>
                      <p className="description">{details.longDesc}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h2 className="talks-title">Talks and/or hands-on labs</h2>
                  <div className="columns is-multiline">
                    {filteredTalks.map(talk => {
                      const color =
                        talk.track === '40-minute Talk' || talk.track === '10-minute Lightning Talk'
                          ? talk.trackInDay
                          : 'workshop';
                      const speakerNames = [];
                      if (talk.speakers) {
                        talk.speakers.forEach(spId => {
                          const foundTalk = speakers.find(sp => sp.id === spId) || {};
                          const speakerName = foundTalk;
                          speakerNames.push(speakerName);
                        });
                      }

                      return (
                        <div key={talk.id} className="column is-3 is-12-touch">
                          <ProgramCard
                            color={color}
                            endTime={talk.endTime}
                            id={talk.id}
                            isConferenceDay={isConferenceDay}
                            isLive={
                              talk.track !== 'Interactive 3-hour Hands-on Lab'
                                ? isLiveCheck(talk, trigger)
                                : false
                            }
                            speakers={speakerNames}
                            startTime={talk.startTime}
                            talk={talk}
                            tileRole=""
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
    // </div>
  );
};

SpeakerDetails.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  loadSpeakerDetails: PropTypes.func.isRequired,
  speakers: PropTypes.arrayOf(PropTypes.any).isRequired,
  talks: PropTypes.arrayOf(PropTypes.any).isRequired
};

const SpeakerDetailsContainer = props => (
  <SpeakerDetailsConsumer>
    {({ isLoading, speakers, talks, loadSpeakerDetails }) => (
      <SpeakerDetails
        isLoading={isLoading}
        loadSpeakerDetails={loadSpeakerDetails}
        speakers={speakers}
        talks={talks}
        {...props}
      />
    )}
  </SpeakerDetailsConsumer>
);

export default SpeakerDetailsContainer;
